import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiMail, FiLoader, FiAlertCircle, FiCheckCircle, FiHome, FiShield, FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { authApi } from '../../services/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      
      await authApi.forgotPassword(email);
      setEmailSent(true);
      
    } catch (err) {
      console.error('Error:', err);
      setEmailSent(true); // Still show success for security
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-primary-50 flex flex-col">
      {/* Header */}
      <header className="w-full py-4 px-6">
        <div className="flex justify-between items-center max-w-7xl mx-auto">
          <Link to="/" className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors">
            <img src="/logo.png" alt="Resumemo" className="h-8 w-auto" />
            <span className="text-xl font-bold">Resumemo</span>
          </Link>
          <nav className="hidden md:flex items-center space-x-6">
            <Link to="/" className="flex items-center space-x-1 text-gray-600 hover:text-primary-600 transition-colors">
              <FiHome className="h-4 w-4" />
              <span>Home</span>
            </Link>
            <Link to="/privacy-policy" className="flex items-center space-x-1 text-gray-600 hover:text-primary-600 transition-colors">
              <FiShield className="h-4 w-4" />
              <span>Privacy</span>
            </Link>
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
              Sign In
            </Link>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-md w-full">
          {!emailSent ? (
            <>
              {/* Logo and Title */}
              <div className="text-center mb-8">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <FiMail className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Forgot password?</h2>
                <p className="text-gray-600">No worries, we'll send you reset instructions</p>
              </div>

              {/* Reset Form */}
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl text-sm flex items-start">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-0.5">
                        <FiAlertCircle className="h-3 w-3" />
                      </div>
                      <div>{error}</div>
                    </div>
                  )}
                  
                  <div>
                    <label htmlFor="email-address" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      id="email-address"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-gray-900 placeholder-gray-400"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <p className="mt-2 text-sm text-gray-500">
                      We'll send a password reset link to this email
                    </p>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-primary-600 hover:from-blue-700 hover:to-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {loading ? (
                      <>
                        <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FiMail className="-ml-1 mr-2 h-4 w-4" />
                        Send Reset Link
                      </>
                    )}
                  </button>
                </form>
              </div>
              
              {/* Back to Sign In */}
              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  className="flex items-center justify-center space-x-2 text-gray-600 hover:text-primary-600 transition-colors"
                >
                  <FiArrowLeft className="h-4 w-4" />
                  <span>Back to sign in</span>
                </Link>
              </div>
            </>
          ) : (
            <>
              {/* Success State */}
              <div className="text-center mb-8">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <FiCheckCircle className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Check your email</h2>
                <p className="text-gray-600">We've sent password reset instructions to your email</p>
              </div>

              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start">
                    <FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-green-900 mb-1">Email sent successfully</p>
                      <p className="text-green-700">
                        If an account exists for <span className="font-medium">{email}</span>, we've sent instructions to reset your password.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4 text-sm text-gray-600">
                  <div>
                    <p className="font-medium text-gray-900 mb-2">What's next?</p>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                          <span className="text-blue-600 font-bold text-xs">1</span>
                        </div>
                        <span>Check your email inbox (and spam folder)</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                          <span className="text-blue-600 font-bold text-xs">2</span>
                        </div>
                        <span>Click the reset link in the email</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                          <span className="text-blue-600 font-bold text-xs">3</span>
                        </div>
                        <span>Create a new password</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-yellow-800 text-xs">
                      <strong>Didn't receive the email?</strong> Check your spam folder or try again with a different email address.
                    </p>
                  </div>
                </div>
                
                <div className="mt-6 text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    <FiArrowLeft className="h-4 w-4" />
                    <span>Back to sign in</span>
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="w-full py-4 px-6 border-t border-gray-200 bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center space-y-2 md:space-y-0">
          <p className="text-sm text-gray-500">© 2026 Resumemo. All rights reserved.</p>
          <div className="flex space-x-4">
            <Link to="/privacy-policy" className="text-sm text-gray-500 hover:text-primary-600 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-sm text-gray-500 hover:text-primary-600 transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ForgotPassword;